import { create } from "zustand";
import { IChatWidget } from "@cma/chat-widgets/types/ChatWidget";

export type ActiveWidgetContext = {
  content: {
    ui: {
      [key: string]: any;
    };
    data: {
      [key: string]: any;
    };
    instructions?: string;
  };
};

export type ChatWidgetDrawerState = "expanded" | "collapsed" | "half-expanded";

interface ChatWidgetStoreState {
  chatWidgetDrawerState: ChatWidgetDrawerState;
  chatWidget: IChatWidget | null;
  isChatWidgetStale: boolean;
  activeWidgetContext: ActiveWidgetContext | null;
  /**
   * Which thread a `temporary` chat widget belongs to — `null` while there's no
   * thread yet. Lets `useOpenLastChatWidget` tell "this client-only widget still
   * belongs to the current session" apart from "the user switched threads".
   */
  temporaryWidgetThreadId: string | null;
}

interface ChatWidgetStoreActions {
  toggleChatWidgetDrawer: (state: ChatWidgetDrawerState) => void;
  setChatWidget: (chatWidget: IChatWidget | null) => void;
  setChatWidgetContext: (context: ActiveWidgetContext | null) => void;
  resetChatWidget: () => void;
  markChatWidgetStale: () => void;
  markChatWidgetFresh: () => void;
  linkTemporaryWidgetThread: (threadId: string) => void;
}

const initialState: ChatWidgetStoreState = {
  isChatWidgetStale: false,
  chatWidgetDrawerState: "collapsed",
  chatWidget: null,
  activeWidgetContext: null,
  temporaryWidgetThreadId: null,
};

type ChatWidgetStore = ChatWidgetStoreState & ChatWidgetStoreActions;

export const useChatWidgetStore = create<ChatWidgetStore>((set) => ({
  ...initialState,
  setChatWidget: (chatWidget) =>
    set(() => ({
      chatWidget,
      activeWidgetContext: null,
      temporaryWidgetThreadId: null,
    })),
  resetChatWidget: () =>
    set(() => ({
      chatWidget: null,
      chatWidgetDrawerState: "collapsed",
      activeWidgetContext: null,
      temporaryWidgetThreadId: null,
    })),
  toggleChatWidgetDrawer: (chatWidgetDrawerState) =>
    set(() => ({
      chatWidgetDrawerState,
    })),
  markChatWidgetStale: () =>
    set(() => ({
      isChatWidgetStale: true,
    })),
  markChatWidgetFresh: () =>
    set(() => ({
      isChatWidgetStale: false,
    })),
  setChatWidgetContext: (activeWidgetContext) =>
    set(() => ({
      activeWidgetContext,
    })),
  linkTemporaryWidgetThread: (threadId) =>
    set(() => ({
      temporaryWidgetThreadId: threadId,
    })),
}));
